import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getUbiquitiWifiDefaultSpeeds } from "../../scripts/api_ubiquiti";

const WIFI_DEFAULT_SPEEDS_QUERY_OPTIONS = {
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 30000,
};

const WIFI_SPEED_ROWS = [
  { band: "5 GHz", bandKey: "5", options: ["20", "40", "80", "160"] },
  { band: "6 GHz", bandKey: "6", options: ["20", "40", "80", "160", "320"] },
];

const getWifiDefaultSpeedBandKey = (row) => {
  const value = row?.band ?? row?.bandKey ?? row?.frequency ?? row?.label;
  const label = String(value || "").toLowerCase();

  if (label.includes("6")) {
    return "6";
  }

  if (label.includes("5")) {
    return "5";
  }

  return "";
};

const normalizeWifiDefaultSpeedRows = (wifiDefaultSpeeds) => {
  const apiRows = Array.isArray(wifiDefaultSpeeds?.rows)
    ? wifiDefaultSpeeds.rows
    : Array.isArray(wifiDefaultSpeeds?.data?.rows)
      ? wifiDefaultSpeeds.data.rows
      : [];

  return WIFI_SPEED_ROWS.map((defaultRow) => {
    const apiRow = apiRows.find((row) => getWifiDefaultSpeedBandKey(row) === defaultRow.bandKey);
    const options = Array.isArray(apiRow?.options) && apiRow.options.length
      ? apiRow.options.map((option) => String(option))
      : defaultRow.options;
    const active = Array.isArray(apiRow?.active)
      ? apiRow.active.map((option) => String(option)).filter((option) => options.includes(option))
      : [];

    return {
      ...defaultRow,
      band: apiRow?.label || defaultRow.band,
      options,
      active,
      evidence: apiRow?.evidence || [],
    };
  });
};

const UbiquitiDefaultWifiSpeeds = ({ apiUrl, enabled = true, onQueryStateChange, refreshKey = 0, siteId }) => {
  const wifiDefaultSpeedsQuery = useQuery({
    ...WIFI_DEFAULT_SPEEDS_QUERY_OPTIONS,
    queryKey: ["ubiquiti-wifi-default-speeds", apiUrl, siteId],
    queryFn: () => getUbiquitiWifiDefaultSpeeds(apiUrl, siteId),
    enabled: enabled && !!apiUrl && !!siteId,
  });
  const { refetch } = wifiDefaultSpeedsQuery;
  const rows = useMemo(
    () => normalizeWifiDefaultSpeedRows(wifiDefaultSpeedsQuery.data),
    [wifiDefaultSpeedsQuery.data],
  );
  const isInitialLoading = !wifiDefaultSpeedsQuery.data && (!enabled || wifiDefaultSpeedsQuery.isLoading);

  useEffect(() => {
    if (enabled && refreshKey && siteId) {
      refetch();
    }
  }, [enabled, refreshKey, refetch, siteId]);

  useEffect(() => {
    if (!onQueryStateChange) {
      return;
    }

    onQueryStateChange({
      siteId,
      isError: wifiDefaultSpeedsQuery.isError,
      isFetched: wifiDefaultSpeedsQuery.isFetched,
      isFetching: wifiDefaultSpeedsQuery.isFetching,
    });
  }, [
    onQueryStateChange,
    siteId,
    wifiDefaultSpeedsQuery.isError,
    wifiDefaultSpeedsQuery.isFetched,
    wifiDefaultSpeedsQuery.isFetching,
  ]);

  useEffect(() => {
    window.__ubiquitiWifiDefaultSpeedsDebug = {
      apiCall: `/ubiquiti/sites/${siteId}/wifi-default-speeds`,
      enabled,
      queryStatus: {
        isLoading: wifiDefaultSpeedsQuery.isLoading,
        isFetching: wifiDefaultSpeedsQuery.isFetching,
        isError: wifiDefaultSpeedsQuery.isError,
        error: wifiDefaultSpeedsQuery.error,
      },
      rawResponse: wifiDefaultSpeedsQuery.data,
      diagnostics: wifiDefaultSpeedsQuery.data?.diagnostics || wifiDefaultSpeedsQuery.data?.data?.diagnostics || null,
      mappedRows: rows,
    };
  }, [
    enabled,
    rows,
    siteId,
    wifiDefaultSpeedsQuery.data,
    wifiDefaultSpeedsQuery.error,
    wifiDefaultSpeedsQuery.isError,
    wifiDefaultSpeedsQuery.isFetching,
    wifiDefaultSpeedsQuery.isLoading,
  ]);

  return (
    <article className="ubiquiti-wifi-speeds-card">
      <div className="ubiquiti-panel-heading">
        Default WiFi Speeds
      </div>
      <small>Channel Widths (MHz)</small>
      {rows.map((row) => (
        <div className="ubiquiti-channel-row" key={row.band}>
          <span>{row.band}</span>
          <div>
            {row.options.map((option) => (
              <strong
                className={[
                  row.active.includes(option) ? "is-active" : "",
                  isInitialLoading ? "is-loading" : "",
                ].filter(Boolean).join(" ")}
                key={option}
              >
                {isInitialLoading ? <span aria-hidden="true"></span> : option}
              </strong>
            ))}
          </div>
        </div>
      ))}
    </article>
  );
};

export default UbiquitiDefaultWifiSpeeds;
